import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Typography,
  Radio,
  FormControlLabel,
  RadioGroup,
  FormControl
} from '@material-ui/core';
import Scrollbar from 'react-perfect-scrollbar';
import { ChevronDoubleRight } from 'components/CustomSVG';
import { colorSchemas } from 'helpers/colorSchemas';
import {
  updateStylePalette,
  setCurrentColorSchemaName
} from 'redux/actions/styles_palette';
import ColorSchemaPreviewPremiumStore from './ColorSchemaPreviewPremiumStore';
import ColorSchemaPreviewRegularStore from './ColorSchemaPreviewRegularStore';
import ColorSchemaPreviewPremiumPlusStore from './ColorSchemaPreviewPremiumPlusStore';
import ColorSchemaPreviewSkinceuticalsStore from './ColorSchemaPreviewSkinceuticalsStore';
import ColorSchemaPreviewModernStore from './ColorSchemaPreviewModernStore';
import ColorSchemaPreviewModernPremiumStore from './ColorSchemaPreviewModernPremiumStore';
import ColorSchemaPreviewModernPremiumPlusStore from './ColorSchemaPreviewModernPremiumPlusStore';


const ColorSchemaPreviewByStoreType = ({colorSchema, storeType}) => {
  if (storeType === 'premium') {
    return <ColorSchemaPreviewPremiumStore />;
  }

  if (storeType === 'premium_plus') {
    return <ColorSchemaPreviewPremiumPlusStore />;
  }

  if (storeType === 'premium_lite') {
    return <ColorSchemaPreviewModernStore />;
  }

  if (storeType === 'skinceuticals') {
    return <ColorSchemaPreviewSkinceuticalsStore colorSchema={colorSchema} />;
  }

  if (storeType === 'modern_premium') {
    return <ColorSchemaPreviewModernPremiumStore />;
  }

  if (storeType === 'modern_premium_plus') {
    return <ColorSchemaPreviewModernPremiumPlusStore />;
  }

  return <ColorSchemaPreviewRegularStore colorSchema={colorSchema} />;
};

const ColorsSchemas = ({onStepChange}) => {
  const dispatch = useDispatch();
  const {
    stylePalette,
    currentStylePalette,
    defaultStylePalette,
    currentSchemaName
  } = useSelector((state) => state.styles_palette);
  const userStore = useSelector((state) => state.user?.profile?.stores?.[0]);
  const allColorSchemas = {
    'Default Schema': defaultStylePalette,
    'Current Schema': currentStylePalette,
    ...colorSchemas
  };

  return (
    <div className="p-8">
      <div className="flex flex-sm-column w-full">
        <div className="border-1-light-grey w-full">
          {Object.keys(stylePalette).length ?
            <ColorSchemaPreviewByStoreType colorSchema={stylePalette} storeType={userStore.storeType} />
            :
            <div className="flex items-center justify-center h-full p-5">
              <span className="text-muted text-16">
                To see preview, please
                select preferred color
                scheme &rarr;
              </span>
            </div>
          }
        </div>
        {!!Object.keys(currentStylePalette).length &&
          <div className="w-full ml-sm-100">
            <Typography variant="body1" className="text-16 mb-2">
              Select preferred color scheme:
            </Typography>
            <FormControl component="div" className="border-1-light-grey max-h-660 min-h-660 p-4 w-full overflow-hidden">
              <Scrollbar
                options={{ suppressScrollX: true }}
              >
                <RadioGroup
                  aria-label="scheme"
                  name="scheme"
                  value={currentSchemaName}
                  onChange={(e) => {
                    const {value} = e.target;
                    dispatch(updateStylePalette(allColorSchemas[value]));
                    dispatch(setCurrentColorSchemaName(value));
                  }}
                >
                  {Object.keys(allColorSchemas).map((schemaName) => {
                    const colorSchema = {
                      headerTopBgColor: allColorSchemas[schemaName].header['theme_design/header/top_border_color'],
                      headerBgColor: allColorSchemas[schemaName].header['theme_design/header/header_bg_color'],
                      headerColor: allColorSchemas[schemaName].header['theme_design/header_primary/text_color'],
                      buttonBgColor: allColorSchemas[schemaName].buttons['theme_design/colors/button_bg_color'],
                      buttonColor: allColorSchemas[schemaName].buttons['theme_design/colors/button_color'],
                      menuBgColor: allColorSchemas[schemaName].menu['theme_design/nav/outer_menu_bg_color'],
                      menuColor: allColorSchemas[schemaName].menu['theme_design/nav/color'],
                      tabBgColor: allColorSchemas[schemaName].product_page['theme_design/product_page/product_tabs_bg_color'],
                      tabColor: allColorSchemas[schemaName].product_page['theme_design/product_page/product_tabs_color'],
                      tabBgColorActive: allColorSchemas[schemaName].product_page['theme_design/product_page/product_tabs_bg_color_active'],
                      tabColorActive: allColorSchemas[schemaName].product_page['theme_design/product_page/product_tabs_color_active'],
                      productNameColor: allColorSchemas[schemaName].category_page['theme_design/colors/product_name_color'],
                      productPriceColor: allColorSchemas[schemaName].category_page['theme_design/colors/price_color'],
                      taglineBgColor: allColorSchemas[schemaName].footer['theme_design/footer_tagline/bg_color'],
                      taglineColor: allColorSchemas[schemaName].footer['theme_design/footer_tagline/color'],
                      footerBgColor: allColorSchemas[schemaName].footer['theme_design/footer/bg_color'],
                      footerColor: allColorSchemas[schemaName].footer['theme_design/footer/color'],
                      footerBottomBgColor: allColorSchemas[schemaName].footer['theme_design/footer_bottom/bg_color'],
                      footerBottomColor: allColorSchemas[schemaName].footer['theme_design/footer_bottom/color'],
                      ctaColor: allColorSchemas[schemaName].homepage_cta['theme_design/cta/cta_mask_bg'],
                      textColor: allColorSchemas[schemaName].colors['theme_design/colors/color_primary_text_color'],
                    };

                    const {headerTopBgColor, headerBgColor, headerColor,
                      buttonBgColor, buttonColor, menuBgColor, menuColor,
                      footerBgColor, footerColor, footerBottomBgColor,
                      footerBottomColor, ctaColor, textColor} = colorSchema;

                    const previewColors = [headerTopBgColor, headerBgColor, headerColor,
                      buttonBgColor, buttonColor, menuBgColor, menuColor, footerBgColor, footerColor, footerBottomBgColor,
                      footerBottomColor, ctaColor, textColor];

                    const uniqueColorsSet = [...new Set(previewColors)];

                    return (
                      <FormControlLabel
                        key={schemaName}
                        value={schemaName}
                        control={<Radio color="primary" />}
                        className="flex w-full my-2"
                        label={
                          <div className="flex items-center">
                            <div className="flex w-full elevation-z15">
                              {uniqueColorsSet.map((color, index) => {
                                return (
                                  <div className="w-full min-h-30" style={{backgroundColor: `${color}`}} key={`${schemaName}-color${index}`} />
                                );
                              })}
                            </div>
                            <div className="flex items-center ml-2 min-w-76 max-w-76 pl-2">{schemaName}</div>
                          </div>
                        }
                      />
                    );
                  })}
                </RadioGroup>
              </Scrollbar>
            </FormControl>
          </div>
        }
      </div>
      <div className="flex items-center justify-end mt-10">
        <Button
          variant="contained"
          color="primary"
          onClick={() => onStepChange(1)}
          title="Next"
          className="px-6 text-white"
          disabled={!Object.keys(stylePalette).length}
        >
          <span className="mr-2 text-16">Next</span>
          <ChevronDoubleRight />
        </Button>
      </div>
    </div>
  );
};

export default ColorsSchemas;
